import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedApi } from './api';
import LoadingSpinner from './components/LoadingSpinner';
import { toast } from 'react-hot-toast';
import './PlugYourWorkSubmit.css';

export default function PlugYourWorkSubmit() {
  const { isAuthenticated, isLoading } = useAuth0();
  const { authenticatedFetch } = useAuthenticatedApi();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    publication: '',
    url: '',
    headline: '',
    blurb: '',
    source: false,
    rate: '',
    editor: ''
  });
  
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/plug-your-work/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          rate: formData.rate ? parseInt(formData.rate) : null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit work');
      }

      toast.success('Submission received and approved!');
      setFormData({
        name: '',
        publication: '',
        url: '',
        headline: '',
        blurb: '',
        source: false,
        rate: '',
        editor: ''
      });
      
      // Scroll to top
      window.scrollTo(0, 0);
      
    } catch (err) {
      toast.error(err.message || 'Failed to submit work');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh'
      }}>
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ 
        padding: '4rem 2rem',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <h1 style={{ color: '#e74c3c', marginBottom: '1rem' }}>Authentication Required</h1>
        <p style={{ color: '#666', marginBottom: '2rem' }}>
          Please log in to submit your work.
        </p>
        <button 
          onClick={() => navigate('/')}
          style={{
            backgroundColor: '#38b44a',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Go to Homepage
        </button>
      </div>
    );
  }

  return (
    <div className="plug-your-work-submit-container" style={{ 
      padding: '2rem',
      maxWidth: '800px',
      margin: '0 auto',
      textAlign: 'left'
    }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ 
          fontSize: '2.5rem',
          fontWeight: '700',
          color: '#333',
          marginBottom: '1rem'
        }}>
          Plug Your Work
        </h1>
        <p style={{ 
          fontSize: '1.125rem',
          color: '#666',
          marginBottom: '1rem'
        }}>
          Enter the URL of a piece you have written below and the Study Hall community will give it a boost!
        </p>
        <p style={{ 
          fontSize: '1rem',
          color: '#666',
          marginBottom: '2rem'
        }}>
          Items will be shared with the community, boosted on our social media accounts, and spotlighted in our blog.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ textAlign: 'left', width: '100%' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block',
            fontSize: '1rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '0.5rem'
          }}>
            Your Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e9ecef',
              borderRadius: '6px',
              fontSize: '1rem',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#38b44a'}
            onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block',
            fontSize: '1rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '0.5rem'
          }}>
            Publication Name *
          </label>
          <input
            type="text"
            name="publication"
            value={formData.publication}
            onChange={handleInputChange}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e9ecef',
              borderRadius: '6px',
              fontSize: '1rem',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#38b44a'}
            onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block',
            fontSize: '1rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '0.5rem'
          }}>
            URL *
          </label>
          <input
            type="url"
            name="url"
            value={formData.url}
            onChange={handleInputChange}
            required
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e9ecef',
              borderRadius: '6px',
              fontSize: '1rem',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#38b44a'}
            onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block',
            fontSize: '1rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '0.5rem'
          }}>
            Editor's Name
          </label>
          <input
            type="text"
            name="editor"
            value={formData.editor}
            onChange={handleInputChange}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e9ecef',
              borderRadius: '6px',
              fontSize: '1rem',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#38b44a'}
            onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block',
            fontSize: '1rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '0.5rem'
          }}>
            Write a first person headline about creating this work *
          </label>
          <input
            type="text"
            name="headline"
            value={formData.headline}
            onChange={handleInputChange}
            required
            placeholder=""
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e9ecef',
              borderRadius: '6px',
              fontSize: '1rem',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#38b44a'}
            onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
          />
          <p style={{ 
            fontSize: '0.875rem',
            color: '#666',
            marginTop: '0.5rem',
            marginBottom: 0
          }}>
            Example: "How I Reported on the Stunning Lack of Regulation in International Shipping"
          </p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block',
            fontSize: '1rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '0.5rem'
          }}>
            Write a brief first person introduction to this article as if you were writing to an audience of journalists. *
          </label>
          <textarea
            name="blurb"
            value={formData.blurb}
            onChange={handleInputChange}
            required
            rows="4"
            placeholder=""
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e9ecef',
              borderRadius: '6px',
              fontSize: '1rem',
              transition: 'border-color 0.2s',
              resize: 'vertical'
            }}
            onFocus={(e) => e.target.style.borderColor = '#38b44a'}
            onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
          />
          <p style={{ 
            fontSize: '0.875rem',
            color: '#666',
            marginTop: '0.5rem',
            marginBottom: 0
          }}>
            Example: "For the L.A. Times I wrote about the burgeoning art poker scene featuring felt tables stacked with underground rounders and celebs like Leonardo DiCaprio, Benny Blanco, and Tobey Maguire."
          </p>
        </div>

        <div className="checkbox-container" style={{ 
          marginBottom: '2rem', 
          width: '100%', 
          textAlign: 'left',
          display: 'block',
          paddingLeft: 0,
          marginLeft: 0
        }}>
          <div style={{ 
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            justifyContent: 'flex-start',
            paddingLeft: 0,
            marginLeft: 0,
            position: 'relative'
          }}>
            <input
              type="checkbox"
              name="source"
              id="source-checkbox"
              checked={formData.source}
              onChange={handleInputChange}
              className="source-checkbox-input"
              style={{
                width: '20px',
                height: '20px',
                minWidth: '20px',
                maxWidth: '20px',
                marginTop: '0.25rem',
                marginRight: '0.75rem',
                marginBottom: '0',
                marginLeft: '0',
                padding: '0',
                flexShrink: '0',
                flexGrow: '0',
                cursor: 'pointer',
                alignSelf: 'flex-start',
                order: '-1'
              }}
            />
            <label 
              htmlFor="source-checkbox"
              style={{ 
                flex: 1,
                lineHeight: '1.5',
                fontSize: '1rem',
                color: '#333',
                cursor: 'pointer',
                margin: 0,
                padding: 0,
                display: 'block',
                textAlign: 'left'
              }}
            >
              Check this box if you heard about this opportunity on Study Hall
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !formData.name || !formData.publication || !formData.url || !formData.headline || !formData.blurb}
          style={{
            backgroundColor: loading ? '#ccc' : '#38b44a',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          {loading && (
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderTop: '2px solid white',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
          )}
          {loading ? 'Submitting...' : 'Submit'}
        </button>

        <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #e9ecef' }}>
          <h2 style={{ 
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '1rem'
          }}>
            Optional:
          </h2>
          <p style={{ 
            fontSize: '1rem',
            color: '#666',
            marginBottom: '1.5rem'
          }}>
            In an effort to increase pay transparency in freelancing, we've partnered with the{' '}
            <a 
              href="https://freelancesolidarity.org/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#38b44a', textDecoration: 'underline' }}
            >
              Freelance Solidarity Project
            </a>{' '}
            to build a rate-sharing database. All submissions are anonymous. If you feel comfortable, please enter the rate you were paid for this piece below.
          </p>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block',
              fontSize: '1rem',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              Rate in USD (optional)
            </label>
            <input
              type="number"
              name="rate"
              value={formData.rate}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                // Allow backspace, delete, tab, escape, enter, and numeric keys
                if (
                  [46, 8, 9, 27, 13].indexOf(e.keyCode) !== -1 ||
                  (e.keyCode === 65 && e.ctrlKey === true) || // Ctrl+A
                  (e.keyCode >= 35 && e.keyCode <= 40) // Home, End, Left, Right, Up, Down
                ) {
                  return;
                }
                // Ensure that it is a number and stop the keypress
                if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                  e.preventDefault();
                }
              }}
              min="0"
              step="1"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '1rem',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#38b44a'}
              onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
            />
          </div>
        </div>
      </form>
    </div>
  );
}
